/* One card, opened.
 *
 * WHAT THE ARTBOARD DRAWS HERE, AND WHAT A CARD IS.
 * The artboard gives a card a three-rung ladder (Regular, Bronze, Gold), a tier
 * chip, a four-digit code of its own, a points catalogue, a switch between
 * stamps and points, and a Leave button. The product's card is one reward at a
 * threshold that repeats: the earn path reads the first tier only and issues
 * the reward the moment the balance reaches it, taking the threshold off the
 * balance. So this screen draws exactly that, and every line on it is a column
 * the API returns.
 *
 * THE CODE IS THE PERSON'S, NOT THE CARD'S. One six-digit code works at every
 * shop, so the hero shows the live one rather than a per-card number.
 */
import { type StoredSecret, t } from "@damga/kit";

import type { Card } from "../App";
import { hueFor, monthYear, onDate, useLiveCode } from "../ui";
import { lastSeen } from "./Wallet";

/* Circles while they fit in a few rows; the points-style bar past that. */
const MAX_CIRCLES = 30;

export function CardDetail({
  card,
  secret,
  landed,
  onBack,
  onRedeem,
}: {
  card: Card;
  secret: StoredSecret | null;
  /** The index of a stamp that landed while this screen was open, to press in. */
  landed: number | null;
  onBack: () => void;
  onRedeem: () => void;
}) {
  const points = card.program_type === "points";
  const closing = card.status === "closing";
  const filled = Math.min(card.balance, card.threshold);
  const circles = !points && card.threshold <= MAX_CIRCLES;
  const live = useLiveCode(secret);

  const facts: { k: string; v: string }[] = [];
  if (typeof card.visits === "number") facts.push({ k: t("detail.fact.visits"), v: String(card.visits) });
  if (card.enrolled_at) facts.push({ k: t("detail.fact.since"), v: monthYear(card.enrolled_at) });
  facts.push({ k: t("detail.fact.ready"), v: String(card.claimable) });
  if (card.last_earn_at) facts.push({ k: t("detail.fact.last"), v: lastSeen(card.last_earn_at) });

  return (
    <>
      <button type="button" className="back" onClick={onBack}>
        ← {t("detail.back")}
      </button>

      <section className="hero" style={{ ["--hue" as string]: hueFor(card.business_id) }}>
        <div className="hero-top">
          <div className="shrink">
            <h2 className="hero-name">{card.business_name}</h2>
            <p className="hero-reward">{card.reward_description}</p>
          </div>
          {card.claimable > 0 ? (
            <span className="chip" data-ready="">
              {t("wallet.readyToClaim")}
            </span>
          ) : closing ? (
            <span className="chip">{t("card.closing.label")}</span>
          ) : null}
        </div>

        {circles ? (
          <ol className="stamps" aria-label={t("detail.progress.stamps", "en", { balance: card.balance, threshold: card.threshold })}>
            {Array.from({ length: card.threshold }, (_, i) => {
              const on = i < filled;
              return (
                <li
                  key={i}
                  className="stamp"
                  data-on={on ? "" : undefined}
                  data-landed={landed === i ? "" : undefined}
                  aria-hidden="true"
                >
                  {on ? "✓" : i + 1}
                </li>
              );
            })}
          </ol>
        ) : (
          <div className="points">
            <p className="points-big">
              <b>{card.balance.toLocaleString("en-GB")}</b>
              <span>/ {card.threshold.toLocaleString("en-GB")}</span>
            </p>
            <span className="bar-track" aria-hidden="true">
              <span
                className="bar-fill"
                style={{ ["--pct" as string]: String(card.threshold ? filled / card.threshold : 0) }}
              />
            </span>
          </div>
        )}

        <div className="hero-foot">
          {/* Progress on the card now, not "ready to claim" again: the chip
              already says that, and after a reward the card has started over. */}
          <span className="hero-progress">
            {t(points ? "detail.progress.points" : "detail.progress.stamps", "en", {
              balance: card.balance,
              threshold: card.threshold,
            })}
          </span>
          {live && (
            <span className="hero-code">
              <small>{t("detail.yourCode")}</small>
              {live.code.slice(0, 3)} {live.code.slice(3)}
              {/* Said with the digits, so they are read together: without it the
                  code on a shop's card reads as that shop's code. */}
              <small className="hero-code-same">{t("detail.sameEverywhere")}</small>
            </span>
          )}
        </div>
      </section>

      {card.claimable > 0 && (
        <button type="button" className="btn btn-primary" onClick={onRedeem}>
          {t("detail.redeem")}
        </button>
      )}

      {closing && (
        <div className="closing" role="note">
          <p>{t("card.closing.stopped")}</p>
          {card.claimable > 0 && card.claimable_until ? (
            <p>{t("card.closing.claimBy", "en", { date: onDate(card.claimable_until) })}</p>
          ) : card.claimable_until ? (
            <p>{t("card.closing.until", "en", { date: onDate(card.claimable_until) })}</p>
          ) : null}
        </div>
      )}

      {card.claimable > 0 && card.reward_expires_at && (
        <p className="notice">{t("wallet.useBy", "en", { date: onDate(card.reward_expires_at) })}</p>
      )}

      <section className="panel">
        <h3 className="label">{t("detail.howTo")}</h3>
        <p className="panel-body">
          {points && card.points_per_stamp
            ? t("detail.howTo.points", "en", { n: card.points_per_stamp })
            : t("detail.howTo.stamps")}
        </p>
        {card.min_gap_minutes ? (
          <p className="panel-body">{t("detail.howTo.gap", "en", { minutes: card.min_gap_minutes })}</p>
        ) : null}
      </section>

      <section className="panel">
        <h3 className="label">{t("detail.reward")}</h3>
        <div className="rung">
          <span className="rung-badge" aria-hidden="true">
            {card.threshold}
          </span>
          <div>
            <p className="rung-title">{card.reward_description}</p>
            <p className="rung-meta">
              {t("detail.reward.repeats", "en", { threshold: card.threshold })}
              {card.reward_validity_days === null || card.reward_validity_days === 0
                ? ` ${t("detail.reward.keeps")}`
                : typeof card.reward_validity_days === "number"
                  ? ` ${t("detail.reward.lasts", "en", { days: card.reward_validity_days })}`
                  : ""}
            </p>
          </div>
        </div>
      </section>

      <dl className="facts">
        {facts.map((f) => (
          <div className="fact" key={f.k}>
            <dt className="fact-k">{f.k}</dt>
            <dd className="fact-v">{f.v}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}
